
import React, { useState } from "react";
import { CalendarSettings } from "@/lib/supabase";
import { UnavailableSlotsMap } from "@/hooks/use-calendar-settings";
import { formSchema } from "./GeneralSettingsTab";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import GeneralSettingsTab from "./GeneralSettingsTab";
import UnavailableSlotsTab from "./UnavailableSlotsTab";

interface CalendarSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: CalendarSettings;
  onSaveSettings: (settings: CalendarSettings) => void;
}

const CalendarSettingsModal: React.FC<CalendarSettingsModalProps> = ({
  open,
  onOpenChange,
  settings,
  onSaveSettings,
}) => {
  const [activeTab, setActiveTab] = useState("generale");
  const [localUnavailableSlots, setLocalUnavailableSlots] = useState<UnavailableSlotsMap>(
    settings.unavailableSlots || {}
  );
  const [formValues, setFormValues] = useState({
    startHour: settings.startHour,
    endHour: settings.endHour,
    workDays: settings.workDays
  });

  const handleGeneralSubmit = (values: z.infer<typeof formSchema>) => {
    // Assicuriamoci che tutte le proprietà richieste siano definite
    const updatedValues = {
      startHour: values.startHour,
      endHour: values.endHour,
      workDays: values.workDays
    };
    
    setFormValues(updatedValues);
    handleSaveSettings(updatedValues);
  };

  const handleUnavailableSlotsSubmit = () => {
    handleSaveSettings(formValues);
  };

  const handleSaveSettings = (values: typeof formValues) => {
    // Qui ci assicuriamo che il valore rispetti l'interfaccia CalendarSettings
    // Manteniamo l'id e lo user_id esistenti se presenti
    const updatedSettings: CalendarSettings = {
      ...settings, // Mantieni i campi esistenti come id e user_id
      startHour: values.startHour,
      endHour: values.endHour,
      workDays: values.workDays,
      unavailableSlots: localUnavailableSlots
    };

    onSaveSettings(updatedSettings);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Impostazioni Calendario</DialogTitle>
          <DialogDescription>
            Modifica le ore di lavoro, i giorni della settimana attivi e le fasce orarie non disponibili.
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-2 w-full mb-4">
            <TabsTrigger value="generale">Impostazioni Generali</TabsTrigger>
            <TabsTrigger value="fasce-non-disponibili">Fasce Non Disponibili</TabsTrigger>
          </TabsList>
          
          <TabsContent value="generale">
            <GeneralSettingsTab 
              defaultValues={formValues}
              onSubmit={handleGeneralSubmit}
            />
          </TabsContent>
          
          <TabsContent value="fasce-non-disponibili">
            <UnavailableSlotsTab 
              localUnavailableSlots={localUnavailableSlots}
              workDays={formValues.workDays}
              onSubmit={handleUnavailableSlotsSubmit}
              onSlotsChange={setLocalUnavailableSlots}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarSettingsModal;
